/**
 * VersionSkewBadge — per-runtime lexruntime build skew vs the platform's
 * ACTIVE image.
 *
 * Renders the lexserver classifier's skew state VERBATIM (we never
 * compute skew). The wire enum keeps stable `"behind"` / `"current"`
 * literals for back-compat with older clients; the user-facing labels
 * deliberately use "active version" semantics — a runtime is either
 * ON the currently-active platform version or DIFFERS from it
 * (rollback ⇒ older active ≠ outdated; this terminology stays accurate
 * across promote AND rollback directions).
 *
 *   current       → green  "on active version"
 *   behind        → amber  "differs from active (X)"  (bare "differs
 *                           from active" only when the target tag is
 *                           genuinely unknown — still amber, never blank)
 *   indeterminate → grey   "version unknown"  (running OR target version/
 *                           commit === "unknown" — classifier's
 *                           "unknown either side" state)
 *   unreachable   → grey   "version unavailable"  (pre-94bd2a32 image OR
 *                           runtime down — NEVER blank / "—" / "latest" /
 *                           implied current)
 *
 * State enum is the lexserver classifier's verbatim output (commit
 * `ea7ab878`): the third state is `indeterminate`, not `unknown`. The
 * `"behind"` literal stays on the wire for back-compat — this badge
 * remaps it to "differs from active" at the label layer only.
 */

import { Badge } from "@/components/ui/badge";
import type { RuntimeSkewState } from "@/types/platform";
import { cn } from "@/lib/utils";

type VersionSkewBadgeProps = {
  skew: RuntimeSkewState;
  /** Platform target tag — only used for the `behind` label. */
  targetVersion?: string | null;
  className?: string;
};

const skewStyles: Record<RuntimeSkewState, string> = {
  current:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800",
  behind:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800",
  indeterminate:
    "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700",
  unreachable:
    "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700",
};

function label(skew: RuntimeSkewState, targetVersion?: string | null): string {
  switch (skew) {
    case "current":
      return "on active version";
    case "behind":
      // Wire-stable `"behind"` literal is remapped to "differs from active"
      // here so the copy stays accurate across BOTH forward-update AND
      // rollback paths. The lexserver enum literal does NOT change.
      return targetVersion
        ? `differs from active (${targetVersion})`
        : "differs from active";
    case "indeterminate":
      return "version unknown";
    case "unreachable":
      return "version unavailable";
  }
}

export function VersionSkewBadge({ skew, targetVersion, className }: VersionSkewBadgeProps) {
  return (
    <Badge
      variant="outline"
      className={cn(skewStyles[skew], className)}
      data-testid="version-skew-badge"
      data-skew={skew}
    >
      {label(skew, targetVersion)}
    </Badge>
  );
}
